import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { equals } from 'ramda'
import { useSelector } from 'hooks/redux'
import { getIsEditing } from 'selectors/navigator'
import { Flex } from 'components/patterns/Layout'
import { classNames } from 'utils/utils'
import strings from 'utils/strings'

import ParameterValue from 'components/ParameterValue'

import { BITS, BYTES } from 'utils/constants'

import {
  getTheRange,
  isInRange,
  isAvailableForInput,
  gridLabel,
  bitValue,
  bitBytePairLabel,
} from 'components/feature/utils/bitGrid'

import withStyle, {
  StyledBit,
  StyledByteLabel,
  StyledSelectionValue,
} from './BitGrid.style'

const BitGrid = ({
  className,
  value,
  selectionHandler,
  groupSize,
  type,
  disabledBits,
  featureModuleName,
}) => {
  const isAvailable = useMemo(
    () => isAvailableForInput(type, groupSize, featureModuleName),
    [type, groupSize, featureModuleName]
  )
  const isEditing = useSelector(getIsEditing)
  const [hoveredRange, setHoveredRange] = useState({})
  const getRange = useMemo(() => getTheRange(groupSize), [groupSize])
  const selectedRange = getRange(value)
  const { startBit, endBit } = selectedRange
  const handleSelection = (index) => {
    if (disabled(index)) return

    const { startBit: rangeStart } = getRange(index)
    selectionHandler(rangeStart)
  }

  const handleSetHoveredRange = (index) => {
    const range = getRange(index)
    const { startBit: rangeStart } = range

    if (isAvailable(rangeStart)) setHoveredRange(range)
  }

  const clearHoveredRange = () => setHoveredRange({})

  const disabled = (index) => {
    if (!isAvailable(index) || (!isEditing && !isInRange(selectedRange, index)))
      return true

    const inHoveredRange = isInRange(hoveredRange)

    if (inHoveredRange(index)) {
      return disabledBits.some(inHoveredRange)
    }

    return disabledBits.includes(index)
  }

  const renderBit = (bit, index) => (
    <StyledBit
      key={bit}
      isEditing={isEditing}
      disabled={disabled(index)}
      occupied={disabledBits.some(equals(index))}
      selected={isInRange(selectedRange, index)}
      hovered={isInRange(hoveredRange, index)}
      onClick={() => handleSelection(index)}
      onMouseEnter={() => handleSetHoveredRange(index)}
      onMouseLeave={clearHoveredRange}
      className={isInRange(selectedRange, index) ? 'selected' : ''}
      data-testid={`bit-grid-bit-${bit}`}
    >
      {bitValue(bit)}
    </StyledBit>
  )

  return (
    <div className={classNames(className, 'bit-grid')}>
      {!isEditing ? (
        <ParameterValue
          className={'can-input-label'}
          label={gridLabel(groupSize, type, value)}
          value={bitBytePairLabel(startBit, endBit)}
          size="small"
        />
      ) : (
        <Flex
          justifyContent="space-between"
          className={'can-input-label editing'}
        >
          <span>{gridLabel(groupSize, type, value)}</span>
          <SelectionValue value={value} range={selectedRange} />
        </Flex>
      )}

      <Flex className={'can-input-bit-grid'}>
        <ByteLabels flexShrink={0} />
        <Flex flexWrap="wrap" flexGrow={1}>
          {BITS.map(renderBit)}
        </Flex>
      </Flex>
    </div>
  )
}

BitGrid.defaultProps = {
  groupSize: 2,
  type: 'output',
  disabledBits: [],
  value: null,
  selectionHandler: () => {},
  resetHandler: () => {},
}

BitGrid.propTypes = {
  /** Classname */
  className: PropTypes.string,
  /** Size of bit group */
  groupSize: PropTypes.number,
  /** Determines functionality of bit hover/selection */
  type: PropTypes.oneOf(['input', 'output']),
  /** Set of disabled bits */
  disabledBits: PropTypes.array,
  /** The initial value/index of the selection */
  value: PropTypes.number,
  /** Handler to set selected value */
  selectionHandler: PropTypes.func,
  /** Handler to reset all relevant props to defaults */
  resetHandler: PropTypes.func,
  /** Module Name to enable level4 bits */
  featureModuleName: PropTypes.string,
}

export default withStyle(BitGrid)

const ByteLabels = () => {
  const byteLabel = (byte) => (
    <StyledByteLabel key={byte}>{`${strings.byte} ${byte}`}</StyledByteLabel>
  )

  return <Flex flexDirection="column">{BYTES.map(byteLabel)}</Flex>
}

const SelectionValue = ({ value, range }) => {
  const { startBit, endBit } = range

  const label = bitBytePairLabel(startBit, endBit)

  return <StyledSelectionValue value={value}>{label}</StyledSelectionValue>
}

SelectionValue.propTypes = {
  value: PropTypes.number,
  range: PropTypes.object,
}
